import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DatePicker from "@/components/DatePicker";
import { Plus, X, Check, XCircle, Repeat, CalendarOff } from "lucide-react";

const TABS = [
  { key: "time_off", label: "Time-Off Requests" },
  { key: "shift_swaps", label: "Shift-Swap Requests" },
];

const STATUS_PILL = {
  pending: "status-progress",
  approved: "status-open",
  rejected: "status-error",
  cancelled: "status-neutral",
};

export default function Requests() {
  const { user } = useAuth();
  const [tab, setTab] = useState("time_off");
  return (
    <div className="space-y-6" data-testid="requests-root">
      <div>
        <div className="label-caps">Workforce</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Requests</h1>
        <div className="text-[color:var(--text-secondary)] text-sm mt-1">
          {user?.role === "employee"
            ? "Submit time-off or shift-swap requests"
            : "Review and act on employee requests"}
        </div>
      </div>
      <div className="flex gap-2 border-b border-[color:var(--border)]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`req-tab-${t.key}`}
                  className={`px-4 py-3 border-b-2 -mb-[1px] ${tab === t.key ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-transparent text-[color:var(--text-secondary)]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "time_off" ? <TimeOffTab user={user}/> : <ShiftSwapTab user={user}/>}
    </div>
  );
}

function TimeOffTab({ user }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/time_off").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, status, comments) => {
    try {
      await api.patch(`/time_off/${id}`, { status, comments });
      toast.success(`Request ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="hos-btn-primary flex items-center gap-2" onClick={() => setCreating(true)} data-testid="new-timeoff-btn">
          <Plus size={16}/> New Time-Off Request
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((r) => (
          <div key={r.id} className="hos-card p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-sm text-[color:var(--gold)]">{r.request_number}</span>
              <span className={`status-pill ${STATUS_PILL[r.status] || "status-neutral"}`} data-testid={`tof-status-${r.id}`}>{r.status}</span>
              <span className="text-xs text-[color:var(--text-secondary)]">{r.employee_name}</span>
              <span className="text-xs text-[color:var(--text-secondary)]">{r.start_date} → {r.end_date}</span>
              {!r.full_day && <span className="text-xs">Partial: {r.time_range}</span>}
            </div>
            {r.reason && <div className="mt-2 text-sm">{r.reason}</div>}
            {r.review_comments && <div className="mt-1 text-xs italic text-[color:var(--text-secondary)]">Review: {r.review_comments}</div>}
            {["admin", "gm", "manager"].includes(user.role) && r.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <button className="hos-btn-primary py-1.5 px-3 flex items-center gap-1" onClick={() => decide(r.id, "approved")} data-testid={`tof-approve-${r.id}`}><Check size={14}/> Approve</button>
                <button className="hos-btn-outline py-1.5 px-3 flex items-center gap-1" onClick={() => {
                  const c = window.prompt("Rejection reason:", "");
                  if (c !== null) decide(r.id, "rejected", c);
                }} data-testid={`tof-reject-${r.id}`} style={{ borderColor: "var(--error)", color: "var(--error)" }}><XCircle size={14}/> Reject</button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]"><CalendarOff className="mx-auto mb-2 text-[color:var(--gold)]" size={22}/>No time-off requests.</div>}
      </div>
      {creating && <TimeOffModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function TimeOffModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    full_day: true,
    time_range: "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/time_off", f);
      toast.success("Request submitted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New Time-Off Request" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-caps">Start</label><DatePicker value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testId="tof-start"/></div>
        <div><label className="label-caps">End</label><DatePicker value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testId="tof-end"/></div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" checked={f.full_day} onChange={(e) => setF({ ...f, full_day: e.target.checked })}/> Full day(s)
      </label>
      {!f.full_day && <input className="hos-input mt-2" placeholder="Time range e.g. 09:00-13:00" value={f.time_range} onChange={(e) => setF({ ...f, time_range: e.target.value })}/>}
      <textarea className="hos-input min-h-[80px] mt-3" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="tof-reason"/>
      <div className="flex justify-end gap-3 mt-4">
        <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
        <button className="hos-btn-primary" onClick={save} disabled={busy} data-testid="tof-submit">Submit</button>
      </div>
    </Modal>
  );
}

function ShiftSwapTab({ user }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/shift_swaps").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, status, comments) => {
    try {
      await api.patch(`/shift_swaps/${id}`, { status, comments });
      toast.success(`Request ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="hos-btn-primary flex items-center gap-2" onClick={() => setCreating(true)} data-testid="new-swap-btn">
          <Plus size={16}/> New Shift-Swap Request
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((r) => (
          <div key={r.id} className="hos-card p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-sm text-[color:var(--gold)]">{r.request_number}</span>
              <span className={`status-pill ${STATUS_PILL[r.status] || "status-neutral"}`} data-testid={`swp-status-${r.id}`}>{r.status}</span>
              <span className="text-xs text-[color:var(--text-secondary)]">{r.employee_name} · {r.request_type}</span>
            </div>
            <div className="mt-2 text-sm text-[color:var(--text-secondary)]">
              Original: {r.original_shift?.date} {r.original_shift?.start_time}–{r.original_shift?.end_time}
              {r.requested_change && <> · Requested: {JSON.stringify(r.requested_change)}</>}
            </div>
            {r.reason && <div className="mt-1 text-sm">{r.reason}</div>}
            {r.review_comments && <div className="mt-1 text-xs italic text-[color:var(--text-secondary)]">Review: {r.review_comments}</div>}
            {["admin", "gm", "manager"].includes(user.role) && r.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <button className="hos-btn-primary py-1.5 px-3 flex items-center gap-1" onClick={() => decide(r.id, "approved")} data-testid={`swp-approve-${r.id}`}><Check size={14}/> Approve</button>
                <button className="hos-btn-outline py-1.5 px-3 flex items-center gap-1" onClick={() => {
                  const c = window.prompt("Rejection reason:", "");
                  if (c !== null) decide(r.id, "rejected", c);
                }} data-testid={`swp-reject-${r.id}`} style={{ borderColor: "var(--error)", color: "var(--error)" }}><XCircle size={14}/> Reject</button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]"><Repeat className="mx-auto mb-2 text-[color:var(--gold)]" size={22}/>No shift-swap requests.</div>}
      </div>
      {creating && <ShiftSwapModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ShiftSwapModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    request_type: "swap",
    original_shift: { date: new Date().toISOString().slice(0, 10), start_time: "10:00", end_time: "18:00" },
    requested_change: {},
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/shift_swaps", f);
      toast.success("Request submitted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New Shift-Swap Request" onClose={onClose}>
      <div className="grid gap-3">
        <div>
          <label className="label-caps">Type</label>
          <select className="hos-input" value={f.request_type} onChange={(e) => setF({ ...f, request_type: e.target.value })} data-testid="swp-type">
            <option value="swap">Shift Swap (with another employee)</option>
            <option value="release">Release (give up shift)</option>
            <option value="time_change">Time Change</option>
            <option value="date_change">Date Change</option>
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="label-caps">Original date</label><DatePicker value={f.original_shift.date} onChange={(v) => setF({ ...f, original_shift: { ...f.original_shift, date: v } })} testId="swp-orig-date"/></div>
          <div><label className="label-caps">Start</label><input type="time" className="hos-input" value={f.original_shift.start_time} onChange={(e) => setF({ ...f, original_shift: { ...f.original_shift, start_time: e.target.value } })}/></div>
          <div><label className="label-caps">End</label><input type="time" className="hos-input" value={f.original_shift.end_time} onChange={(e) => setF({ ...f, original_shift: { ...f.original_shift, end_time: e.target.value } })}/></div>
        </div>
        {(f.request_type === "time_change" || f.request_type === "date_change") && (
          <div>
            <label className="label-caps">Requested change (JSON e.g. {"{\"date\":\"2026-08-01\"}"} )</label>
            <input className="hos-input" placeholder='{"date":"2026-08-01"}' onChange={(e) => {
              try { setF({ ...f, requested_change: JSON.parse(e.target.value || "{}") }); }
              catch (_) {}
            }}/>
          </div>
        )}
        <textarea className="hos-input min-h-[80px]" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="swp-reason"/>
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
        <button className="hos-btn-primary" onClick={save} disabled={busy} data-testid="swp-submit">Submit</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">{title}</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
